import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  FormControl,
  FormLabel,
  useColorModeValue,
  useToast,
  Alert,
  AlertIcon,
  AlertDescription,
  AlertTitle,
  Code,
  IconButton,
  InputGroup,
  InputRightElement,
  Divider,
  Link,
  Spinner,
  FormHelperText,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon, CopyIcon, CheckIcon, ExternalLinkIcon, InfoIcon } from "@chakra-ui/icons";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import CloudMevWorkerSettings from "./CloudMevWorkerSettings";
import PageShell from "../layout/PageShell";

interface CloudflareConfig {
  apiToken: string;
  accountId: string;
  zoneId: string;
  origin: string;
  updatedAt?: any;
  updatedBy?: string;
}

const NUAGEPOS_ZONE_ID = "72ae93a7aa159bc78d556f9070314302";
const REPLACED_ZONE_ID = "4f6756843d76023462a32f9e7bd161c3";
const FALLBACK_ORIGIN = "fallback.nuagepos.com";

const normalizeZoneId = (zoneId?: string) => {
  const value = (zoneId || "").trim().toLowerCase();
  if (!value || value === REPLACED_ZONE_ID) return NUAGEPOS_ZONE_ID;
  return value;
};

const normalizeOrigin = (origin?: string) => {
  const value = (origin || "").trim().toLowerCase();
  if (!value || value.includes("flashmenu")) return FALLBACK_ORIGIN;
  return value;
};

const CloudflareSettings: React.FC = () => {
  const toast = useToast();
  const [config, setConfig] = useState<CloudflareConfig>({
    apiToken: "",
    accountId: "",
    zoneId: "",
    origin: "",
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiToken, setShowApiToken] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [idsUnlocked, setIdsUnlocked] = useState(false);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockText, setUnlockText] = useState("");
  const unlockCancelRef = useRef<HTMLButtonElement>(null);

  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const infoBgColor = useColorModeValue("blue.50", "blue.900");

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const configDoc = await getDoc(doc(db, "systemConfig", "cloudflare"));
      if (configDoc.exists()) {
        const data = configDoc.data() as CloudflareConfig;
        const zoneId = normalizeZoneId(data.zoneId);
        const origin = normalizeOrigin(data.origin);
        setConfig({
          apiToken: data.apiToken || "",
          accountId: data.accountId || "",
          zoneId,
          origin,
        });
        if (data.zoneId !== zoneId || data.origin !== origin) {
          await setDoc(doc(db, "systemConfig", "cloudflare"), {
            zoneId,
            origin,
            updatedAt: serverTimestamp(),
          }, { merge: true });
        }
      }
    } catch (error) {
      console.error("Error loading Cloudflare config:", error);
      toast({
        title: "Error",
        description: "Failed to load configuration",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    // Validation
    if (!config.apiToken || !config.accountId || !config.zoneId || !config.origin) {
      toast({
        title: "Validation Error",
        description: "All fields are required",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSaving(true);

    try {
      const origin = normalizeOrigin(config.origin);
      const zoneId = normalizeZoneId(config.zoneId);
      await setDoc(doc(db, "systemConfig", "cloudflare"), {
        ...config,
        zoneId,
        origin,
        updatedAt: serverTimestamp(),
        updatedBy: "super_admin",
      });

      toast({
        title: "Success",
        description: "Cloudflare configuration saved successfully!",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error saving Cloudflare config:", error);
      toast({
        title: "Error",
        description: "Failed to save configuration",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = (field: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopiedField(field);
    toast({
      title: "Copied!",
      description: "Value copied to clipboard",
      status: "success",
      duration: 2000,
      isClosable: true,
    });
    setTimeout(() => setCopiedField(null), 2000);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <>
    <PageShell
      title="Cloudflare Settings"
      description="Custom hostnames for store domains, and the MEV Worker used for Revenu Québec enrollment."
      wide
    >
      <VStack align="stretch" spacing={6}>

        <Tabs colorScheme="blue" variant="enclosed">
          <TabList>
            <Tab>Custom Hostnames</Tab>
            <Tab>Worker</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0} pt={6}>
        <VStack align="stretch" spacing={6}>
        <Alert status="info" borderRadius="md">
          <AlertIcon as={InfoIcon} />
          <Box>
            <AlertTitle fontSize="sm">Setup Instructions:</AlertTitle>
            <AlertDescription fontSize="xs">
              1. Enable <strong>Cloudflare for SaaS</strong> in your Cloudflare dashboard (SSL/TLS → Custom Hostnames)
              <br />
              2. Create a CNAME record: <Code>fallback.nuagepos.com</Code> → <Code>nuageposonline.web.app</Code> (Proxied)
              <br />
              3. Set the Custom Hostname Fallback Origin to: <Code>fallback.nuagepos.com</Code>
              <br />
              4. Create an API token: My Profile → API Tokens → Create Token. Permissions: Zone → SSL and Certificates → Edit, and Zone → Zone → Read, for nuagepos.com. DNS Edit alone cannot create custom hostnames.
              <br />
              5. Account ID is the first ID in the dashboard URL: dash.cloudflare.com/<strong>account-id</strong>/nuagepos.com. It is also under nuagepos.com → Overview → API → Account ID
              <br />
              6. Zone ID is <strong>not</strong> in the URL. Open nuagepos.com → Overview → API → Zone ID
            </AlertDescription>
          </Box>
        </Alert>

        <Box bg={bgColor} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={6}>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel fontSize="sm">Cloudflare API Token</FormLabel>
              <InputGroup>
                <Input
                  type={showApiToken ? "text" : "password"}
                  value={config.apiToken}
                  onChange={(e) => setConfig({ ...config, apiToken: e.target.value })}
                  placeholder="Enter your Cloudflare API token"
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showApiToken ? "Hide token" : "Show token"}
                    icon={showApiToken ? <ViewOffIcon /> : <ViewIcon />}
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowApiToken(!showApiToken)}
                  />
                </InputRightElement>
              </InputGroup>
              <FormHelperText fontSize="xs">
                Token with Zone → SSL and Certificates → Edit for nuagepos.com
              </FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Cloudflare Account ID</FormLabel>
              <InputGroup>
                <Input
                  value={config.accountId}
                  onChange={(e) => setConfig({ ...config, accountId: e.target.value })}
                  placeholder="Enter your Cloudflare account ID"
                  isReadOnly={!idsUnlocked}
                  bg={!idsUnlocked ? "gray.50" : undefined}
                  onClick={() => { if (!idsUnlocked) setUnlockOpen(true); }}
                />
                {config.accountId && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Copy account ID"
                      icon={copiedField === "accountId" ? <CheckIcon color="green.500" /> : <CopyIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCopy("accountId", config.accountId)}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormHelperText fontSize="xs">
                Overview → API → Account ID. The same value is the first ID in the Cloudflare URL.
              </FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Cloudflare Zone ID (nuagepos.com)</FormLabel>
              <InputGroup>
                <Input
                  value={config.zoneId}
                  onChange={(e) => setConfig({ ...config, zoneId: e.target.value })}
                  placeholder="Enter Zone ID for nuagepos.com"
                  isReadOnly={!idsUnlocked}
                  bg={!idsUnlocked ? "gray.50" : undefined}
                  onClick={() => { if (!idsUnlocked) setUnlockOpen(true); }}
                />
                {config.zoneId && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Copy zone ID"
                      icon={copiedField === "zoneId" ? <CheckIcon color="green.500" /> : <CopyIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCopy("zoneId", config.zoneId)}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormHelperText fontSize="xs">
                nuagepos.com → Overview → API → Zone ID. Do not copy the ID from the URL. That one is the Account ID.
              </FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm">Custom Hostname Origin (Fallback Origin)</FormLabel>
              <InputGroup>
                <Input
                  value={config.origin}
                  onChange={(e) => setConfig({ ...config, origin: e.target.value })}
                  placeholder="fallback.nuagepos.com"
                />
                {config.origin && (
                  <InputRightElement>
                    <IconButton
                      aria-label="Copy origin"
                      icon={copiedField === "origin" ? <CheckIcon color="green.500" /> : <CopyIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => handleCopy("origin", config.origin)}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
              <FormHelperText fontSize="xs">
                The Fallback Origin from Cloudflare Custom Hostnames (e.g., fallback.nuagepos.com)
              </FormHelperText>
            </FormControl>

            {!idsUnlocked && (
              <Button variant="outline" colorScheme="red" alignSelf="flex-start" onClick={() => setUnlockOpen(true)}>
                Change account ID or zone ID
              </Button>
            )}

            <Divider my={4} />

            <HStack justify="space-between">
              <Text fontSize="xs" color="gray.500">
                Configuration will be used by all custom domain functions
              </Text>
              <Button
                colorScheme="blue"
                onClick={handleSave}
                isLoading={saving}
                loadingText="Saving..."
                isDisabled={!config.apiToken || !config.accountId || !config.zoneId || !config.origin}
              >
                Save Configuration
              </Button>
            </HStack>
          </VStack>
        </Box>

        <Box bg={infoBgColor} borderRadius="lg" p={4}>
          <Text fontSize="md" fontWeight="semibold" mb={3}>
            Quick Links
          </Text>
          <VStack align="stretch" spacing={2}>
            <Link href="https://dash.cloudflare.com" isExternal color="blue.600">
              → Cloudflare Dashboard <ExternalLinkIcon mx={1} />
            </Link>
            <Link href="https://dash.cloudflare.com/profile/api-tokens" isExternal color="blue.600">
              → Create API Token <ExternalLinkIcon mx={1} />
            </Link>
            <Link
              href="https://developers.cloudflare.com/cloudflare-for-platforms/cloudflare-for-saas/"
              isExternal
              color="blue.600"
            >
              → Cloudflare for SaaS Documentation <ExternalLinkIcon mx={1} />
            </Link>
          </VStack>
        </Box>

        <Alert status="warning" borderRadius="md">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Important Notes:</AlertTitle>
            <AlertDescription fontSize="xs">
              • Keep your API token secure - never share it publicly
              <br />
              • The API token needs "Zone → DNS → Edit" and "Zone → SSL and Certificates → Edit" permissions
              <br />
              • Changes to this configuration will affect all stores using custom domains
              <br />• First 100 custom hostnames are FREE with Cloudflare for SaaS
            </AlertDescription>
          </Box>
        </Alert>
        </VStack>
            </TabPanel>
            <TabPanel px={0} pt={6}>
              <CloudMevWorkerSettings />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>
    </PageShell>

      <AlertDialog
        isOpen={unlockOpen}
        leastDestructiveRef={unlockCancelRef}
        onClose={() => {
          setUnlockOpen(false);
          setUnlockText("");
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              These IDs control every store domain
            </AlertDialogHeader>
            <AlertDialogBody>
              <VStack align="stretch" spacing={3}>
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription fontSize="sm">
                    A wrong Zone ID creates custom hostnames in another Cloudflare zone. The Custom Hostnames page for nuagepos.com will stay empty.
                  </AlertDescription>
                </Alert>
                <Text fontSize="sm">
                  Account ID is the first ID in the dashboard URL, and it is also under Overview → API → Account ID.
                </Text>
                <Text fontSize="sm">
                  Zone ID is only under nuagepos.com → Overview → API → Zone ID. The ID in the URL is not the Zone ID.
                </Text>
                <FormControl>
                  <FormLabel fontSize="sm">Type the word change to unlock these fields</FormLabel>
                  <Input
                    value={unlockText}
                    onChange={(e) => setUnlockText(e.target.value)}
                    placeholder="change"
                    autoComplete="off"
                  />
                </FormControl>
              </VStack>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={unlockCancelRef} onClick={() => { setUnlockOpen(false); setUnlockText(""); }}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                ml={3}
                isDisabled={unlockText !== "change"}
                onClick={() => {
                  setIdsUnlocked(true);
                  setUnlockOpen(false);
                  setUnlockText("");
                }}
              >
                Unlock
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default CloudflareSettings;
